// MARK://dhk8/admin-shell/lib/clients/provisioning
// /signup → POST /v1/provisioning/tenants (ProvisionTenantInput) → poll
// GET /v1/provisioning/tenants/runs/:id (9 steps). Wires dhk8-platform-provisioning.
// Preview: a simulated run that advances one step per poll and hands back an owner token.
//
// The provisioning saga MUST create the tenant in `mode: SANDBOX` (sample catalog seeded,
// sales don't count). The merchant later flips it to LIVE from the admin (tenants.goLive).
import { svcUrl, apiData } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'

export interface ProvisionTenantInput {
  businessName: string
  legalName: string
  rfc: string
  ownerEmail: string
  ownerName: string
  phone: string
  sku: string
  vertical: 'restaurant'
  timezone: string
  currency: string
  address?: string
  /** Contraseña que el dueño eligió en el signup — el provisioning la pasa a provision-owner
   *  para que pueda volver a entrar al admin con correo+contraseña. */
  password?: string
}

export const PROVISION_STEPS = [
  'Creando la cuenta del comerciante',   // tenant
  'Creando al usuario dueño',            // identity
  'Configurando licencia y módulos',     // licensing
  'Configurando ajustes fiscales',       // config
  'Sembrando el menú de ejemplo',        // catalog
  'Generando el plano de mesas base',    // floor
  'Registrando la terminal POS',         // terminals
  'Notificando a la red',                // event
  'Finalizando y emitiendo token de acceso', // result
] as const

export type RunStatus = 'pending' | 'running' | 'succeeded' | 'failed'
export interface ProvisionRun {
  id: string
  status: RunStatus
  stepIndex: number          // 0-based index of the step in progress / last done
  steps: { name: string; state: 'pending' | 'running' | 'done' | 'failed' }[]
  tenantId?: string
  ownerToken?: string
  ownerUserId?: string
  /** ID de cuenta POS de 4 dígitos del dueño. */
  posPinId?: string
  /** PIN de login/desbloqueo POS de 6 dígitos del dueño. */
  posLoginPin?: string
  error?: string
}

// ── preview simulated run registry ─────────────────────────────────────────
const runs = new Map<string, { startedAt: number; input: ProvisionTenantInput }>()

function snapshot(runId: string): ProvisionRun {
  const r = runs.get(runId)
  if (!r) return { id: runId, status: 'failed', stepIndex: 0, steps: [], error: 'run no encontrado' }
  const elapsedSteps = Math.min(PROVISION_STEPS.length, Math.floor((Date.now() - r.startedAt) / 700) + 1)
  const done = elapsedSteps >= PROVISION_STEPS.length
  const steps = PROVISION_STEPS.map((name, i) => ({
    name,
    state: (i < elapsedSteps - 1 ? 'done' : i === elapsedSteps - 1 && !done ? 'running' : done ? 'done' : 'pending') as 'pending' | 'running' | 'done' | 'failed',
  }))
  return {
    id: runId,
    status: done ? 'succeeded' : 'running',
    stepIndex: Math.max(0, elapsedSteps - 1),
    steps,
    ...(done ? { tenantId: 'tnt_' + runId.slice(0, 8), ownerToken: 'demo-owner-token-' + runId.slice(0, 6), ownerUserId: 'usr_owner_' + runId.slice(0, 6) } : {}),
  }
}

export async function provisionTenant(input: ProvisionTenantInput): Promise<{ runId: string }> {
  if (PREVIEW_STUB) {
    const runId = 'run-' + Math.random().toString(36).slice(2, 12)
    runs.set(runId, { startedAt: Date.now(), input })
    return mock({ runId }, 250)
  }
  const r = await apiData<{ runId: string } | { id: string }>(svcUrl('dhk8-platform-provisioning', '/v1/provisioning/tenants'), { method: 'POST', body: input })
  return { runId: 'runId' in r ? r.runId : r.id }
}

// Pasos canónicos del orquestador (backend), en orden — 1:1 con PROVISION_STEPS por índice.
const CANON_STEPS = ['tenant', 'identity', 'licensing', 'config', 'catalog', 'floor', 'terminals', 'event', 'result'] as const

interface RawProvisionRun {
  runId: string
  tenantId?: string
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED'
  stepsCompleted?: string[]
  result?: { accountId?: string; slug?: string; testTenantId?: string; liveTenantId?: string; ownerLoginToken?: string; ownerUserId?: string; posPinId?: string; posLoginPin?: string } | null
  error?: string | null
}

/** Adapta la respuesta del servicio ({status:DONE|..., stepsCompleted, result}) a la forma ProvisionRun que usa el wizard. */
export async function getProvisionRun(runId: string, signal?: AbortSignal): Promise<ProvisionRun> {
  if (PREVIEW_STUB) return mock(snapshot(runId), 120)
  const raw = await apiData<RawProvisionRun>(svcUrl('dhk8-platform-provisioning', `/v1/provisioning/tenants/runs/${runId}`), { signal })
  const completed = new Set(raw.stepsCompleted ?? [])
  const done = raw.status === 'DONE'
  const failed = raw.status === 'FAILED'
  const runningIdx = completed.size
  const steps = CANON_STEPS.map((canon, i) => ({
    name: PROVISION_STEPS[i],
    state: (done || completed.has(canon)
      ? 'done'
      : failed && i === runningIdx
        ? 'failed'
        : !failed && i === runningIdx
          ? 'running'
          : 'pending') as 'pending' | 'running' | 'done' | 'failed',
  }))
  const tenantId = raw.result?.testTenantId ?? raw.tenantId
  return {
    id: raw.runId,
    status: done ? 'succeeded' : failed ? 'failed' : 'running',
    stepIndex: Math.max(0, runningIdx - (done ? 1 : 0)),
    steps,
    ...(tenantId ? { tenantId } : {}),
    ...(raw.result?.ownerLoginToken ? { ownerToken: raw.result.ownerLoginToken } : {}),
    ...(raw.result?.ownerUserId ? { ownerUserId: raw.result.ownerUserId } : {}),
    ...(raw.result?.posPinId ? { posPinId: raw.result.posPinId } : {}),
    ...(raw.result?.posLoginPin ? { posLoginPin: raw.result.posLoginPin } : {}),
    ...(failed && raw.error ? { error: raw.error } : {}),
  }
}
